import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { Expense, FilterState, Category, User, Tenant, AuthState } from '../types';
import { CategoryTree, FlatCategory } from '../types/category';

interface AppState {
  expenses: Expense[];
  categories: Category[];
  categoryTree: CategoryTree | null;
  flatCategories: {
    categories1: FlatCategory[];
    categories2: FlatCategory[];
    categories3: FlatCategory[];
    allCategories: FlatCategory[];
  } | null;
  filters: FilterState;
  currentUser: User | null;
  selectedExpense: Expense | null;
  auth: AuthState;
}

type AppAction =
  | { type: 'SET_EXPENSES'; payload: Expense[] }
  | { type: 'ADD_EXPENSE'; payload: Expense }
  | { type: 'UPDATE_EXPENSE'; payload: Expense }
  | { type: 'DELETE_EXPENSE'; payload: string }
  | { type: 'SET_FILTERS'; payload: Partial<FilterState> }
  | { type: 'CLEAR_FILTERS' }
  | { type: 'SET_SELECTED_EXPENSE'; payload: Expense | null }
  | { type: 'SET_CATEGORIES'; payload: Category[] }
  | { type: 'SET_CATEGORY_TREE'; payload: { tree: CategoryTree; flattened: AppState['flatCategories'] } }
  | { type: 'SET_CURRENT_USER'; payload: User | null }
  | { type: 'SET_AUTH_STATE'; payload: AuthState }
  | { type: 'LOGIN'; payload: { user: User; tenant: Tenant; token: string } }
  | { type: 'LOGOUT' };

const initialState: AppState = {
  expenses: [],
  categories: [],
  categoryTree: null,
  flatCategories: null,
  filters: {
    serialNumber: '',
    submittedBy: [],
    vendorName: '',
    documentNumber: '',
    amountFrom: '',
    amountTo: '',
    creationDateFrom: '',
    creationDateTo: '',
    documentDateFrom: '',
    documentDateTo: '',
    notes: '',
    category1: [],
    category2: [],
    category3: [],
  },
  currentUser: null,
  selectedExpense: null,
  auth: {
    user: null,
    tenant: null,
    token: null,
    isAuthenticated: false,
  },
};

// Helper functions for localStorage
const saveAuthToStorage = (auth: AuthState) => {
  localStorage.setItem('metrica_auth', JSON.stringify(auth));
};

const loadAuthFromStorage = (): AuthState | null => {
  const stored = localStorage.getItem('metrica_auth');
  return stored ? JSON.parse(stored) : null;
};

const loadTenantFromStorage = (): Tenant | null => {
  const stored = localStorage.getItem('metrica_tenant');
  return stored ? JSON.parse(stored) : null;
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_EXPENSES':
      return { ...state, expenses: action.payload };
    case 'ADD_EXPENSE':
      return { ...state, expenses: [...state.expenses, action.payload] };
    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense.id === action.payload.id ? action.payload : expense
        ),
      };
    case 'DELETE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense.id !== action.payload),
      };
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    case 'CLEAR_FILTERS':
      return { ...state, filters: initialState.filters };
    case 'SET_SELECTED_EXPENSE':
      return { ...state, selectedExpense: action.payload };
    case 'SET_CATEGORIES':
      return { ...state, categories: action.payload };
    case 'SET_CATEGORY_TREE':
      return { 
        ...state, 
        categoryTree: action.payload.tree,
        flatCategories: action.payload.flattened
      };
    case 'SET_CURRENT_USER':
      return { ...state, currentUser: action.payload };
    case 'SET_AUTH_STATE':
      const newAuthState = { ...state, auth: action.payload };
      saveAuthToStorage(action.payload);
      return newAuthState;
    case 'LOGIN':
      const loginState = {
        ...state,
        auth: {
          user: action.payload.user,
          tenant: action.payload.tenant,
          token: action.payload.token,
          isAuthenticated: true,
        },
        currentUser: action.payload.user,
      };
      saveAuthToStorage(loginState.auth);
      return loginState;
    case 'LOGOUT':
      const logoutState = {
        ...state,
        auth: {
          user: null,
          tenant: null,
          token: null,
          isAuthenticated: false,
        },
        currentUser: null,
      };
      localStorage.removeItem('metrica_auth');
      return logoutState;
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Load authentication state from localStorage on app start
  useEffect(() => {
    const storedAuth = loadAuthFromStorage();
    const storedTenant = loadTenantFromStorage();
    
    if (storedAuth && storedAuth.token) {
      // If we have tenant information, include it in the auth state
      if (storedTenant) {
        const authWithTenant = {
          ...storedAuth,
          tenant: storedTenant
        };
        dispatch({ type: 'SET_AUTH_STATE', payload: authWithTenant });
      } else {
        dispatch({ type: 'SET_AUTH_STATE', payload: storedAuth });
      }
    }
  }, []);

  // Listen for auth state changes from AuthContext
  useEffect(() => {
    const handleStorageChange = () => {
      const storedAuth = loadAuthFromStorage();
      if (storedAuth && storedAuth.token) {
        dispatch({ type: 'SET_AUTH_STATE', payload: storedAuth });
      }
    };

    // Listen for storage events (when AuthContext updates localStorage)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (for same-tab communication)
    window.addEventListener('authStateChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authStateChanged', handleStorageChange);
    };
  }, [dispatch]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
} 